/**
 * 알람 목 데이터.
 *
 * 🔴 **서버 DTO 모양을 그대로 따른다**(2026-09-20 실물 대조로 고침).
 *    종전 fixture 는 실물보다 **순했다** — 그래서 화면의 틀림이 데모를 통과했다.
 *
 * | 종전 fixture | 실물 | 왜 문제였나 |
 * | --- | --- | --- |
 * | `category: 'critical'` 등 | 그때는 **없는 필드**(2026-09-26 부터 온다 — 아래) | 탭 3종이 이 값으로 갈렸는데 실물엔 안 왔다 |
 * | `suggested_actions: [...]` | **없는 필드** | 「조치 가이드」가 데모에만 떴다 |
 * | `timestamp` | **없는 필드** | 실물은 `last_ts` 뿐이다 |
 * | `source: 'cent'`·`'stain'`·`'robot'` | `'*'`·`'orchestrator'`·`'module.centrifuge'` | 별칭표에 없는 값이라 **화면에 원값이 그대로 나온다.** fixture 가 예쁜 값을 써서 그게 안 보였다 |
 *
 * 관측 근거 — `GET /api/v1/alarms` 실물 4건의 **키 합집합 17개**가 이 파일의 상한이다:
 * `alarm_id · state · severity · source · message · error_code · ack_required · auto_clear ·
 *  ack_by · ack_at · cleared_at · grouping_key · occurrence_count ·
 *  last_ts · resolved · version`
 * (`event_id` 는 계약에 있으나 실물 4건에는 안 실렸다 — 그래서 한 건에만 둔다)
 *
 * 🔵 **`category` — 2026-09-26 부터 새 적재분에 실린다**(`raise_alarm` 이 `catalog_category(error_code)` 로 채움).
 *   값은 **각 코드의 카탈로그 분류 그대로**다(`ctcells-core app/core/error_codes.py` 에서 확인).
 *   🔴 옛 적재분은 **키째 없다**(소급 불가) — 그 꼴을 `ALARM_MQTT_DISCONNECT` 한 건이 지킨다. 채우지 마라.
 *
 * ⚠ **서버가 안 만드는 키를 여기 넣지 마라.** 넣는 순간 「응답에 있는 값」으로 읽힌다.
 * ⚠ `message` 는 서버가 준 문장을 그대로 담는다 — 화면이 문장을 만들지 않는다.
 */
import type { AlarmEvent } from '@ctcells/contract'

/**
 * 실물에서 가장 흔한 꼴 — 카탈로그가 `source: '*'` 로 못박은 코드다.
 * ⚠ `ack_required: false` + `auto_clear: true` 라 **[확인] 버튼이 아니라 「확인 불필요」**가 뜬다.
 */
export const ALARM_UNLOAD_PENDING: AlarmEvent = {
  // v0.9.3 정본 필수 칸(2026-10-07) — `actionable` = 분류가 `user_recoverable` 인가 · 조치 문구는 카탈로그 인용이라 목엔 없다
  actionable: true,
  suggested_actions: [],
  alarm_id: '50a0aa1a-ddf5-4335-9fe2-41672628069c',
  state: 'active',
  severity: 'WARN',
  source: '*',
  module_id: null, // 계약 0.28.15 칸(2026-10-22) — 서버가 늘 싣는다 · 사건 모듈을 모르면 null
  error_code: 'UNLOAD_PENDING',
  category: 'user_recoverable', // 카탈로그 UNLOAD_PENDING.category
  message: '산출물 수거 대기 — 수거존에 트레이가 도달했습니다',
  ack_required: false,
  auto_clear: true,
  // 🔴 건 단위 키다 — 뭉치면 다른 대기가 서로를 삼킨다(`module_subscriber._raise_unload_pending`)
  grouping_key: 'UNLOAD_PENDING:tray_unload:3',
  occurrence_count: 1,
  last_ts: '2026-09-20T13:33:44.918476+00:00',
  resolved: false,
  version: 0,
}

/**
 * 이미 해제된 건 — `ack_by`·`ack_at`·`cleared_at` 이 차는 꼴.
 * 🔴 **`category` 키가 없다 — 일부러다.** 2026-09-22 이전 적재분은 실물에서 키째 안 온다(소급 불가).
 *    화면은 이 건을 「전체」에만 보여야 한다(어느 분류로도 접지 않는다).
 */
export const ALARM_MQTT_DISCONNECT: AlarmEvent = {
  // v0.9.3 정본 필수 칸(2026-10-07) — `actionable` = 분류가 `user_recoverable` 인가 · 조치 문구는 카탈로그 인용이라 목엔 없다
  actionable: false,
  suggested_actions: [],
  alarm_id: 'c90c71d5-abe7-4011-af05-026e0666c847',
  state: 'cleared',
  severity: 'WARN',
  source: '*',
  module_id: null, // 계약 0.28.15 칸(2026-10-22) — 서버가 늘 싣는다 · 사건 모듈을 모르면 null
  error_code: 'MQTT_DISCONNECT',
  message: 'MQTT 브로커 연결 끊김 (LWT)',
  ack_required: false,
  auto_clear: true,
  // ⚠ 사람이 아니다 — 서버가 스스로 확인한 건이 실제로 이렇게 온다
  ack_by: 'system:module_link',
  ack_at: '2026-09-20T03:02:08.286916+00:00',
  cleared_at: '2026-09-20T03:02:08.286916+00:00',
  grouping_key: 'mqtt:l2-subscription',
  occurrence_count: 2,
  last_ts: '2026-09-17T08:21:46.950897+00:00',
  resolved: false,
  version: 2,
}

/**
 * 확인은 됐고 해제는 안 된 건 — `state: 'acknowledged'` 표기 확인용.
 * ⚠ **실물 실값은 0건이다**(2026-09-20 `?state=acknowledged` → `total_count: 0`).
 *   서버가 낼 수 있는 상태라 목에는 두지만, 「실물에서 본 적 있다」로 읽지 마라.
 * ⚠ `source` 는 카탈로그의 `module.centrifuge` 를 쓴다 — **별칭표에 없어서 화면에 원값이
 *   그대로 나온다.** 그것이 실물의 모습이고, 고쳐야 할 것은 fixture 가 아니라 별칭표다.
 */
export const ALARM_LOAD_OVERDUE: AlarmEvent = {
  // v0.9.3 정본 필수 칸(2026-10-07) — `actionable` = 분류가 `user_recoverable` 인가 · 조치 문구는 카탈로그 인용이라 목엔 없다
  actionable: true,
  suggested_actions: [],
  alarm_id: 'alm-71aa-0000-4000-8000-00000000071a',
  event_id: 'evt-71aa',
  state: 'acknowledged',
  severity: 'ERROR',
  source: 'module.centrifuge',
  module_id: null, // 계약 0.28.15 칸(2026-10-22) — 서버가 늘 싣는다 · 사건 모듈을 모르면 null
  error_code: 'LOAD_OVERDUE',
  category: 'user_recoverable', // 카탈로그 LOAD_OVERDUE.category
  message: '입고 적재 지연 — 사람 적재 대기가 임계 시간을 넘겼습니다',
  ack_required: true,
  auto_clear: false,
  ack_by: 'dev-operator',
  ack_at: '2026-09-20T05:31:20+00:00',
  grouping_key: 'LOAD_OVERDUE:module.centrifuge',
  occurrence_count: 1,
  last_ts: '2026-09-20T05:09:12+00:00',
  resolved: false,
  version: 2,
}

/**
 * latching — `auto_clear: false` 라 **[해제] 버튼이 뜨는 유일한 갈래**다.
 * 🔴 `resolved: false` 이므로 그 버튼은 **비활성**이고 사유는 「조건 미해소」다.
 *    자동 해제·자동 재개는 금지다(DP-02).
 * ⚠ 종전에는 이 갈래를 `category: 'critical'` 로 갈랐는데 **그 필드가 실물에 없다.**
 *   `auto_clear` 가 그 뜻을 갖는 실필드다(`SM-ALM-05` 「미지정 = latching(자동 소멸 안 함)」).
 */
export const ALARM_CENT_IMBALANCE: AlarmEvent = {
  // v0.9.3 정본 필수 칸(2026-10-07) — `actionable` = 분류가 `user_recoverable` 인가 · 조치 문구는 카탈로그 인용이라 목엔 없다
  actionable: false,
  suggested_actions: [],
  alarm_id: 'alm-9f3c-0000-4000-8000-00000000009f',
  event_id: 'evt-9f3c',
  state: 'acknowledged',
  severity: 'CRITICAL',
  source: 'module.centrifuge',
  module_id: null, // 계약 0.28.15 칸(2026-10-22) — 서버가 늘 싣는다 · 사건 모듈을 모르면 null
  error_code: 'CENT_IMBALANCE',
  category: 'critical', // 카탈로그 CENT_IMBALANCE.category
  message: '원심기 로터의 균형이 맞지 않습니다', // 카탈로그 17.1.3 `CENT_IMBALANCE.message`(2026-10-22)
  device_error: {
    driver: 'centrifuge_rs232_driver',
    map: 'syserror_map',
    code: '32',
    band: '30-34',
  },
  ack_required: true,
  auto_clear: false,
  ack_by: 'dev-operator',
  ack_at: '2026-09-20T05:32:10+00:00',
  grouping_key: 'CENT_IMBALANCE:module.centrifuge',
  occurrence_count: 1,
  last_ts: '2026-09-20T05:31:59+00:00',
  resolved: false,
  version: 1,
}

export const ALARMS_RUNNING: AlarmEvent[] = [ALARM_UNLOAD_PENDING, ALARM_MQTT_DISCONNECT]
export const ALARMS_IDLE: AlarmEvent[] = []
export const ALARMS_READY: AlarmEvent[] = [ALARM_LOAD_OVERDUE]
export const ALARMS_ERROR: AlarmEvent[] = [ALARM_CENT_IMBALANCE]

/** 전 알람 — 알람 화면의 「전체」 탭과 이력 화면이 같이 쓴다 */
export const ALARMS_ALL: AlarmEvent[] = [
  ALARM_CENT_IMBALANCE,
  ALARM_LOAD_OVERDUE,
  ALARM_MQTT_DISCONNECT,
  ALARM_UNLOAD_PENDING,
]
